import React, { useState } from 'react';
import {
  ChoiceGroup,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  IChoiceGroupOption,
  IChoiceGroupOptionStyleProps,
  IChoiceGroupOptionStyles,
  IconButton,
  makeStyles,
  Stack,
  TooltipHost,
} from '@fluentui/react';
import { EntityAction } from 'common/types/utility';
import { SuccessButton } from 'common/components/SuccessButton';

interface ActivateNotifyCycleModalProps {
  action?: EntityAction;
  onConfirm: (isActive: boolean) => void;
  iconName?: string;
  visible?: boolean;
}

const useStyles = makeStyles(() => ({
  iconButton: {
    marginRight: 10,
  },
}));

const optionStyle = (
  props: IChoiceGroupOptionStyleProps
): Partial<IChoiceGroupOptionStyles> => ({
  field: {
    fontWeight: props.checked ? 600 : 400,
  },
  root: {
    whiteSpace: 'nowrap',
  },
});

export const options: IChoiceGroupOption[] = [
  {
    key: 'Activate',
    text: 'Activate',
    styles: optionStyle,
  },
  {
    key: 'Deactivate',
    text: 'Deactivate',
    styles: optionStyle,
  },
];

export const ActivateNotifyCycleModal: React.FC<
  ActivateNotifyCycleModalProps
> = ({
  action = EntityAction.Delete,
  onConfirm,
  visible = true,
}) => {
    const styles = useStyles();

    const [dialogVisibility, setDialogVisibility] = useState<boolean>(true);
    const [isActive, setIsActive] = useState<boolean>(true);

    const handleChange = (
      ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
      option?: IChoiceGroupOption
    ) => {
      setIsActive(option?.key === 'Activate');
    };

    const SUBTEXT =
      'Choose the activation status for the selected cycles and press confirm to update:';

    return (
      <Stack horizontal>
        {visible && (
          <TooltipHost content="Change the activation status">
            <>
              <IconButton
                onClick={() => setDialogVisibility(!dialogVisibility)}
                iconProps={{ iconName: 'SyncStatus' }}
                ariaLabel={action}
                className={styles.iconButton}
              />
            </>
          </TooltipHost>
        )}

        {!dialogVisibility && (
          <Dialog
            hidden={false}
            onDismiss={() => setDialogVisibility(!dialogVisibility)}
            dialogContentProps={{
              title: 'Update Activation Status',
              type: DialogType.largeHeader,
              subText: SUBTEXT,
            }}
          >
            <ChoiceGroup
              options={options}
              selectedKey={isActive ? 'Activate' : 'Deactivate'}
              onChange={handleChange}
              styles={{
                root: {
                  marginTop: '-15px',
                },
                flexContainer: {
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  gap: '20px',
                },
              }}
            />

            <DialogFooter
              styles={{
                actions: {
                  marginTop: '30px',
                },
              }}
            >
              <SuccessButton
                onClick={() => {
                  onConfirm(isActive);
                  setDialogVisibility(!dialogVisibility);
                }}
                text={action}
              />

              <DefaultButton
                onClick={() => setDialogVisibility(!dialogVisibility)}
                text="Cancel"
              />
            </DialogFooter>
          </Dialog>
        )}
      </Stack>
    );
  };